import { useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { Check, Loader2, RotateCw, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { formatDateTime } from '@/lib/dates'
import { proposalError, useApplyProposal, useDismissProposal, useStartInvestigation } from '@/queries/investigations'
import type { DismissReason, ProposedAction, StaleChange } from '@/types/investigation'

export const DISMISS_REASONS: { value: DismissReason; label: string }[] = [
  { value: 'wrong_diagnosis', label: 'Wrong diagnosis' },
  { value: 'already_fixed', label: 'Already fixed' },
  { value: 'not_needed', label: 'Not needed' }
]

// Shown in full: staff approve exactly what Apply will write, so nothing is cut short
export function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'empty'
  return typeof value === 'string' ? `"${value}"` : typeof value === 'object' ? JSON.stringify(value) : String(value)
}

export function targetOf(proposal: ProposedAction): string {
  return proposal.targetLabel ? `${proposal.targetLabel} (${proposal.targetType})` : `${proposal.targetType} ${proposal.targetId}`
}

export function ChangeList({ proposal }: { proposal: ProposedAction }) {
  return (
    <dl
      className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-2 gap-y-0.5 rounded-md bg-etn-surface-sunken p-2 font-mono text-xs"
      data-testid="proposal-diff"
    >
      {proposal.changes.map(change => (
        <div key={change.field} className="contents" data-testid="proposal-change">
          <dt className="text-etn-text-soft">{change.field}</dt>
          <dd className="whitespace-pre-wrap break-words">
            <span className="text-etn-danger-text line-through">{formatValue(change.before)}</span> →{' '}
            <span className="text-etn-success-text">{formatValue(change.after)}</span>
          </dd>
        </div>
      ))}
      {(proposal.details ?? []).map(detail => (
        <div key={detail.label} className="contents" data-testid="proposal-detail">
          <dt className="text-etn-text-soft">{detail.label}</dt>
          <dd className="whitespace-pre-wrap break-words">{formatValue(detail.value)}</dd>
        </div>
      ))}
    </dl>
  )
}

// Far longer than an apply takes; the API lets a proposal stuck this long in `applying` be closed
const STRANDED_APPLY_MS = 5 * 60 * 1000

function StaleNotice({ changes, onDismiss, busy, ticketId }: { changes: StaleChange[]; onDismiss: () => void; busy: boolean; ticketId: string }) {
  const rerun = useStartInvestigation(ticketId)
  return (
    <div className="space-y-2 rounded-md bg-etn-warning-subtle p-2 text-xs text-etn-warning-text" data-testid="proposal-stale" role="status">
      {changes.map(change => (
        <p key={change.field}>
          Changed since the investigation: <span className="font-medium">{change.field}</span> expected {formatValue(change.expected)}, now{' '}
          {formatValue(change.current)}
        </p>
      ))}
      <p>It can no longer be applied.</p>
      <div className="flex gap-2">
        <Button size="xs" variant="outline" disabled={rerun.isPending} onClick={() => rerun.mutate()}>
          <RotateCw />
          Run again
        </Button>
        <Button size="xs" variant="outline" disabled={busy} onClick={onDismiss}>
          <X />
          Dismiss
        </Button>
      </div>
    </div>
  )
}

/**
 * Apply and Dismiss for one proposal. Apply first shows exactly what will change, on which record,
 * and whose permissions it runs with; every outcome (stale, refused, applied) lands inline here.
 */
export function ProposalControls({ ticketId, invId, proposal }: { ticketId: string; invId: string; proposal: ProposedAction }) {
  const { user } = useAuth0()
  const apply = useApplyProposal(ticketId, invId)
  const dismiss = useDismissProposal(ticketId, invId)
  const [mode, setMode] = useState<'idle' | 'confirm' | 'dismiss'>('idle')
  const [reason, setReason] = useState<DismissReason | null>(null)
  const [note, setNote] = useState('')

  // The mutation's answer is newer than the list until the refetch lands
  const decided = apply.data?.proposal ?? dismiss.data?.proposal
  const shown = proposal.status === 'proposed' && decided ? decided : proposal
  const applyError = apply.error ? proposalError(apply.error, 'Could not apply the change') : null
  const dismissError = dismiss.error ? proposalError(dismiss.error, 'Could not dismiss the proposal') : null
  const staleChanges =
    shown.status === 'stale' ? (shown.staleChanges ?? []) : applyError?.code === 'STALE_PROPOSAL' ? (applyError.staleChanges ?? []) : null
  const busy = apply.isPending || dismiss.isPending

  if (shown.status === 'applied') {
    return (
      <p className="text-xs text-etn-success-text" data-testid="proposal-outcome">
        Applied by {shown.decidedByName || 'a staff member'}
        {shown.decidedAt ? `, ${formatDateTime(shown.decidedAt)}` : ''}
      </p>
    )
  }

  if (shown.status === 'dismissed') {
    const why = [DISMISS_REASONS.find(r => r.value === shown.dismissReason)?.label, shown.dismissNote].filter(Boolean).join(': ')
    return (
      <p className="text-xs text-etn-text-soft" data-testid="proposal-outcome">
        Dismissed by {shown.decidedByName || 'a staff member'}
        {shown.decidedAt ? `, ${formatDateTime(shown.decidedAt)}` : ''}
        {why ? `. ${why}` : ''}
      </p>
    )
  }

  const stranded = shown.status === 'applying' && Date.now() - Date.parse(shown.decidedAt ?? '') > STRANDED_APPLY_MS
  if (shown.status === 'applying' && !stranded) return <p className="text-xs text-etn-text-soft">Being applied</p>

  const dismissPanel = (
    <div className="space-y-2 rounded-md border border-etn-border-hairline p-2" data-testid="dismiss-panel">
      <p className="text-xs font-medium">Why dismiss it? (optional)</p>
      <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Reason">
        {DISMISS_REASONS.map(r => (
          <button
            key={r.value}
            type="button"
            role="radio"
            aria-checked={reason === r.value}
            onClick={() => setReason(current => (current === r.value ? null : r.value))}
            className={`rounded-etn-pill px-2.5 py-0.5 text-etn-micro font-semibold ${
              reason === r.value ? 'bg-etn-accent-subtle text-etn-accent-text-active' : 'bg-etn-control-subtle text-etn-text-cell'
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>
      <input
        aria-label="Note"
        value={note}
        maxLength={500}
        onChange={e => setNote(e.target.value)}
        placeholder="Add a short note"
        className="h-[var(--etn-control-height-sm)] w-full rounded-etn-pill border border-etn-border-input bg-etn-surface px-3 text-xs"
      />
      {dismissError && <p className="text-xs text-etn-danger-text">{dismissError.message}</p>}
      <div className="flex gap-2">
        <Button
          size="xs"
          variant="outline"
          disabled={busy}
          onClick={() => dismiss.mutate({ proposalId: shown._id, reason: note.trim() && !reason ? 'other' : (reason ?? undefined), note })}
        >
          {dismiss.isPending ? <Loader2 className="animate-spin" /> : <X />}
          Dismiss proposal
        </Button>
        <Button size="xs" variant="ghost" disabled={busy} onClick={() => setMode('idle')}>
          Cancel
        </Button>
      </div>
    </div>
  )

  if (staleChanges) {
    return mode === 'dismiss' ? (
      dismissPanel
    ) : (
      <StaleNotice changes={staleChanges} busy={busy} ticketId={ticketId} onDismiss={() => setMode('dismiss')} />
    )
  }

  // Only a proposal still open can be confirmed: a refused apply refetches as failed
  if (mode === 'confirm' && shown.status === 'proposed') {
    return (
      <div className="space-y-2 rounded-md border border-etn-border-hairline p-2" data-testid="apply-confirm">
        <ChangeList proposal={shown} />
        <p className="text-xs text-etn-text-soft">On {targetOf(shown)}</p>
        <p className="text-xs">This will run as {user?.name ?? 'you'}, with your permissions.</p>
        {applyError && <p className="text-xs text-etn-danger-text">{applyError.message}</p>}
        <div className="flex gap-2">
          <Button size="xs" disabled={busy} onClick={() => apply.mutate(shown._id)}>
            {apply.isPending ? <Loader2 className="animate-spin" /> : <Check />}
            Confirm and apply
          </Button>
          <Button size="xs" variant="ghost" disabled={busy} onClick={() => setMode('idle')}>
            Cancel
          </Button>
        </div>
      </div>
    )
  }

  if (mode === 'dismiss') return dismissPanel

  return (
    <div className="space-y-1">
      {shown.status === 'failed' && shown.error && <p className="text-xs text-etn-danger-text">{shown.error}</p>}
      {stranded && <p className="text-xs text-etn-warning-text">This apply was interrupted, so its outcome is unknown. Check the record, then close it.</p>}
      <div className="flex gap-2">
        {shown.status === 'proposed' && (
          <Button size="xs" disabled={busy} onClick={() => setMode('confirm')}>
            <Check />
            Apply
          </Button>
        )}
        <Button size="xs" variant="ghost" disabled={busy} onClick={() => setMode('dismiss')}>
          <X />
          Dismiss
        </Button>
      </div>
    </div>
  )
}
